import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const socialLinks = [
    { 
      name: 'Facebook', 
      url: 'https://www.facebook.com/profile.php?id=61577508516641',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    { 
      name: 'Instagram', 
      url: 'https://www.instagram.com/vbrkisanplanttech/',
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
        </svg>
      )
    },
    { 
      name: 'YouTube', 
      url: 'https://www.youtube.com/@vbrgroup-official',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.377.505 9.377.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      )
    }
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Kisan', path: '/about' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Divisions', path: '/divisions' },
    { name: 'Projects', path: '/projects' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Insights', path: '/insights' },
    { name: 'Contact', path: '/contact' },
  ];

  const divisions = [
    { name: 'Kisan FARM360', path: '/divisions/farm360' },
    { name: 'GREENHABITAT 360', path: '/divisions/greenhabitat-360' },
    { name: 'Kisan AGRIQ', path: '/divisions/agriq' },
    { name: 'Kisan VIGORIQ', path: '/divisions/vigoriq' },
    { name: 'Kisan VEDVAN', path: '/divisions/vedvan' },
    { name: 'Kisan PLANTIQ', path: '/divisions/plantiq' },
    { name: 'Kisan ORGANIQ', path: '/divisions/organiq' },
    { name: 'Kisan IRRIQ', path: '/divisions/irriq' },
    { name: 'Kisan SECURE', path: '/divisions/secure' }    
  ];

  return (
    <footer className="relative bg-text-dark pt-16 pb-8 overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary-green to-transparent opacity-50"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-20 mb-10">
          
          {/* Brand Column */}
          <div className="space-y-6">
            <div className="bg-white/10 p-4 rounded-2xl w-fit backdrop-blur-sm border border-white/5">
              <img src={logo} alt="Kisan Plant Technologies" className="h-14 w-auto brightness-0 invert"  loading="lazy" decoding="async" />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              Pioneering the future of sustainable agriculture through intelligent technology and ecological innovation.
            </p>
            <div className="pt-2">
              <h4 className="text-white font-bold text-lg mb-4">Connect With Kisan</h4>
              <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a 
                  key={social.name} 
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 transition-all duration-300 hover:bg-primary-green hover:text-white border border-white/5`}
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
              </div>
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h4 className="text-white font-bold text-lg mb-8 relative w-fit">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-primary-green"></span>
            </h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="text-gray-400 hover:text-primary-green transition-colors text-sm font-medium flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-600 group-hover:bg-primary-green transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Divisions Column */}
          <div>
            <h4 className="text-white font-bold text-lg mb-8 relative w-fit">
              Our Divisions
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-primary-green"></span>
            </h4>
            <ul className="grid grid-cols-1 gap-4">
              {divisions.map((div) => (
                <li key={div.name}>
                  <Link 
                    to={div.path} 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="text-gray-400 hover:text-primary-green transition-colors text-sm font-medium flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-600 group-hover:bg-primary-green transition-colors"></span>
                    {div.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-white font-bold text-lg mb-8 relative w-fit">
              Get In Touch
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-primary-green"></span>
            </h4>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-primary-green shrink-0 border border-white/5">
                  <MapPin className="w-5 h-5" />
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Kisan Plant Technologies Pvt. Ltd., Plot no 11 Sneha Residency, Behind kalyani motors, LB Nagar, Hyderabad-500068
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-primary-green shrink-0 border border-white/5">
                  <Phone className="w-5 h-5" />
                </div>
                <a href="tel:+919492023333" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">
                  +91 9492023333
                </a>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-primary-green shrink-0 border border-white/5">
                  <Mail className="w-5 h-5" />
                </div>
                <a href="https://mail.google.com/mail/?view=cm&fs=1&to=info@kisanplant.technology" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">
                  info@kisanplant.technology
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-gray-500 text-xs font-medium">
            © {new Date().getFullYear()} Kisan Plant Technologies. All Rights Reserved.
          </p>
          <div className="flex gap-8">
            <Link to="#" className="text-gray-500 hover:text-white text-xs transition-colors">Privacy Policy</Link>
            <Link to="#" className="text-gray-500 hover:text-white text-xs transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
